import {
    Box,
    Stack,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {D9Page} from '@/pages/components/audit-program/d-section/constants';
import {D9_PAGE} from '@/pages/components/audit-program/d-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface D9ConfirmationViewProps {
    header: AuditProgramSectionVO;
    page: D9Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

/** D9 — Specimen debtor / creditor confirmation letter. */
export default function D9ConfirmationView({
    header,
    page,
    refCode = 'D9',
    values,
    onChange,
}: D9ConfirmationViewProps) {
    const meta = {
        title: page.title || D9_PAGE.title,
        greeting: page.greeting || D9_PAGE.greeting,
        bodyIntro: page.bodyIntro || D9_PAGE.bodyIntro,
        bullets: page.bullets?.length ? page.bullets : D9_PAGE.bullets,
        closing: page.closing?.length ? page.closing : D9_PAGE.closing,
    };

    return (
        <Box sx={{p: 2, maxWidth: 720}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 2, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{minWidth: 72}}>Our ref:</Typography>
                    <InputField
                        type="text"
                        fullWidth
                        compact
                        value={values.ourRef ?? ''}
                        onChange={(e) => onChange('ourRef', e.target.value)}
                    />
                </Stack>

                <Box>
                    <Typography variant="body2" fontWeight={600} sx={{mb: 0.5}}>
                        Name and address of lender / borrower
                    </Typography>
                    <InputField
                        type="text"
                        fullWidth
                        compact
                        multiline
                        minRows={3}
                        value={values.addressee ?? ''}
                        onChange={(e) => onChange('addressee', e.target.value)}
                    />
                </Box>

                <Typography variant="body2">{meta.greeting}</Typography>

                <InputField
                    type="text"
                    fullWidth
                    compact
                    placeholder="Name of client company"
                    value={values.clientCompany ?? values.client ?? ''}
                    onChange={(e) => onChange('clientCompany', e.target.value)}
                />

                <Typography variant="body2" sx={{lineHeight: 1.5}}>{meta.bodyIntro}</Typography>

                <Stack spacing={0.25} sx={{pl: 1}}>
                    {meta.bullets.map((b, idx) => (
                        <Typography key={idx} variant="body2">• {b}</Typography>
                    ))}
                </Stack>

                {meta.closing.map((line, idx) => (
                    <Typography key={idx} variant="body2" color={idx === meta.closing.length - 1 ? 'text.secondary' : 'text.primary'}>
                        {line}
                    </Typography>
                ))}

                <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                        Reply / notes
                    </Typography>
                    <InputField
                        type="text"
                        fullWidth
                        compact
                        multiline
                        minRows={4}
                        value={values.replyNotes ?? ''}
                        onChange={(e) => onChange('replyNotes', e.target.value)}
                    />
                </Box>
            </Stack>
        </Box>
    );
}
